import React, { useEffect, useState } from 'react';
import Button from '@app/react-common/components/button/Button';
import { ListAbTests } from './ab-tests-list/AbTestsList';
import useAbTests from './hooks/useAbTests';
import { AbTestModal } from '../ab-test-modal/AbTestModal';
import { AbTestModalV2 } from '../ab-test-modal/AbTestModalV2';
import { useAppContext } from '@app/react-common/AppContext';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import { createPortal } from 'react-dom';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';
import useCheckouts from '../../../../checkout/react/hooks/useCheckouts';
import Dropdown from '@app/react-common/components/dropdown/Dropdown';
import { AbTestTypeCategory, AbTestWidgetType } from '../../models/AbTestCreationType';
import useShop from '@app/react-common/hooks/useShop';
import useAbTestTypeOptions from './hooks/useAbTestTypeOptions';
import { getShopCountryCode } from '@app/pages/checkout-app/utils/string.utils';
import { useAbTestingApi } from '../../api/useAbTestingApi';
import { shouldUseAbTestModalV2 } from '../../utils';

interface AbTestsViewProps {}

export const AbTestsView = ({}: AbTestsViewProps) => {
  const {
    draftAbTests,
    concludedAbTests,
    runningAbTests,
    abTestActions,
    apiErrorNotification,
    setApiErrorNotification,
    freeShippingThreshold,
    bigSpenderThreshold,
    fetchFreeShippingThreshold,
    fetchBigSpenderThreshold,
  } = useAbTests();
  const [createAbTestIsOpen, setCreateAbTestIsOpen] = useState<boolean>(false);
  const [selectedTestType, setSelectedTestType] = useState<AbTestTypeCategory>(AbTestTypeCategory.SHIPPING_RATES);
  const [runningAbTestCheckoutIds, setRunningAbTestCheckoutIds] = useState<number[]>([]);
  const [testsLoading, setTestsLoading] = useState<boolean>(true);
  const { showFullPageLoader } = useAppContext();
  const { checkoutSettings } = useCheckouts();
  const shop = useShop();
  const useAbTestModalV2 = shouldUseAbTestModalV2(shop?.metadata, selectedTestType);
  const [enablePredictedSegmentAbTestWinner, setEnablePredictedSegmentAbTestWinner] = useState(false);
  const [hasThemePermission, setHasThemePermission] = useState<boolean>(true);
  const abTestingApi = useAbTestingApi(false);
  const abTestTypeOptions = useAbTestTypeOptions();

  useEffect(() => {
    // Fetch test data
    showFullPageLoader(true);

    Promise.all([
      abTestActions.fetchDraftTests(),
      abTestActions.fetchRunningTests(),
      abTestingApi
        .get('/ab-tests/permissions/themes')
        .then((response) => {
          setHasThemePermission(response?.data?.hasPermission ?? false);
        })
        .catch(() => {
          setHasThemePermission(false);
        }),
    ]).then(() => {
      showFullPageLoader(false);
      setTestsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (shop) {
      setEnablePredictedSegmentAbTestWinner(shop?.metadata?.enablePredictedSegmentAbTestWinner !== false);

      // Fetch threshold data when shop is available
      const countryCode = getShopCountryCode(shop as any);
      fetchFreeShippingThreshold(countryCode);
      fetchBigSpenderThreshold();
    }
  }, [shop]);

  useEffect(() => {
    if (runningAbTests) {
      const runningAbTestCheckoutIds = runningAbTests
        .map((abTest) =>
          abTest.abTestVariants
            .map((variant) =>
              variant.abTestWidgets
                .filter((x) => x.type === AbTestWidgetType.CHECKOUT)
                .map((x) => x.externalId)
                .flat(),
            )
            .flat(),
        )
        .flat();
      setRunningAbTestCheckoutIds(runningAbTestCheckoutIds);
    }
  }, [runningAbTests]);

  return (
    <div className="ab-tests-view">
      {checkoutSettings?.isTestMode && (
        <WarningBanner variant="regular" title={'Shipping methods in test mode'} content={''}></WarningBanner>
      )}
      <div className="ab-tests-view__header">
        {createPortal(
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div className="ab-test-dropdown-wrapper">
              <Dropdown
                placeholder="New Test"
                variant="menu"
                options={abTestTypeOptions}
                onSelect={(option) => {
                  if (option) {
                    setSelectedTestType(option.value as AbTestTypeCategory);
                    setCreateAbTestIsOpen(true);
                  }
                }}
              />
            </div>
          </div>,
          document.getElementById('header-portal-content'),
        )}
        <div className="ab-tests-view__header__actions">
          <Button
            className="ab-tests-view__header__howitworks"
            onClick={() => {
              window.open('https://support.prettydamnquick.com/en/articles/9209106-setting-up-an-a-b-test', '_blank');
            }}
            disabled={false}
            text={'How it works'}
            size={'medium'}
            iconUrl="/assets/icons/external.svg"
            variant={'tertiary'}
          />
        </div>
        {createAbTestIsOpen &&
          (useAbTestModalV2 ? (
            <AbTestModalV2
              key={selectedTestType}
              isOpen={createAbTestIsOpen}
              onClose={() => {
                setCreateAbTestIsOpen(false);
              }}
              onSaveSuccess={() => {
                setCreateAbTestIsOpen(false);
              }}
              saveTest={abTestActions.upsertTest}
              mode={'create'}
              runningAbTestCheckoutIds={runningAbTestCheckoutIds}
              onTestTypeChange={setSelectedTestType}
            />
          ) : (
            <AbTestModal
              key={selectedTestType}
              isOpen={createAbTestIsOpen}
              onClose={() => {
                setCreateAbTestIsOpen(false);
              }}
              onSaveSuccess={() => {
                setCreateAbTestIsOpen(false);
              }}
              saveTest={abTestActions.upsertTest}
              mode={'create'}
              testTypeCategory={selectedTestType}
              runningAbTestCheckoutIds={runningAbTestCheckoutIds}
              hasThemePermission={hasThemePermission}
              onTestTypeChange={setSelectedTestType}
            />
          ))}
      </div>
      {!testsLoading && (
        <ListAbTests
          draftAbTests={draftAbTests}
          concludedAbTests={concludedAbTests}
          runningAbTests={runningAbTests}
          abTestActions={abTestActions}
          runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          enablePredictedSegmentAbTestWinner={enablePredictedSegmentAbTestWinner}
          freeShippingThreshold={freeShippingThreshold}
          bigSpenderThreshold={bigSpenderThreshold}></ListAbTests>
      )}
      {apiErrorNotification && (
        <ConfirmationModal
          title={apiErrorNotification.title}
          buttonText={'Ok'}
          mainContent={apiErrorNotification.content}
          isOpen={apiErrorNotification != null}
          onClose={() => {
            setApiErrorNotification(null);
          }}
          onSuccess={() => {
            setApiErrorNotification(null);
          }}
        />
      )}
    </div>
  );
};
